import type { FC, ReactElement } from 'react';
import { Button, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import type { FormMode } from './formMode';

const useStyles = makeStyles({
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
});

export interface FormActionsProps {
  /** create/edit/view (form-standard.context.md §2) — view mode hides Submit and relabels Cancel
   *  to Close (no dirty state possible in view mode). */
  mode: FormMode;
  /** `{entity}-form` — used to derive the `-submit`/`-cancel` data-testid convention (§9). */
  testIdPrefix: string;
  isSubmitting: boolean;
  onCancel?: () => void;
  submitLabel: string;
  submittingLabel: string;
  submitIcon?: ReactElement;
}

/** Shared Cancel/Submit action row for the `{Entity}FormDialog` scaffold (form-standard.context.md §6). */
export const FormActions: FC<FormActionsProps> = ({
  mode,
  testIdPrefix,
  isSubmitting,
  onCancel,
  submitLabel,
  submittingLabel,
  submitIcon,
}) => {
  const styles = useStyles();
  return (
    <div className={styles.actions}>
      {onCancel && (
        <Button
          appearance="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
          data-testid={`${testIdPrefix}-cancel`}
        >
          {mode === 'view' ? 'Close' : 'Cancel'}
        </Button>
      )}
      {mode !== 'view' && (
        <Button
          type="submit"
          appearance="primary"
          icon={isSubmitting ? <Spinner size="tiny" /> : submitIcon}
          disabled={isSubmitting}
          data-testid={`${testIdPrefix}-submit`}
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      )}
    </div>
  );
};
